import { NextComponentType, NextPageContext } from 'next';
import * as React from 'react';

import MerchantCourseDetailsPageProtected from '../../components/merchants/bookingServices/MerchantCourseDetailsPageProtected';
import implUCMerchantRepository from '../../infrastructure/repositories/UCMerchantRepository';
import Error, { Props } from '../../pages/_error';

interface QueryProps {
  session: string;
  query: {
    publicId: string;
    resource_id: string;
  };
}

type State = StateInitial | StateBookAvailable | StateError;

interface StateInitial {
  type: 'initial';
}

interface StateBookAvailable {
  type: 'available';
  success: boolean;
  accepting: boolean;
}

interface StateError {
  type: 'error';
  statusCode: Props['statusCode'];
}

type Component = typeof React.Component | NextComponentType;

export default function<WrappedProps>(WrappedComponent: Component) {
  const isNext = (c: Component): c is NextComponentType =>
    c.hasOwnProperty('getInitialProps');

  type BookAvailableProps = WrappedProps & QueryProps;

  return class BookAvailable extends React.Component<
    BookAvailableProps,
    State
  > {
    constructor(props: BookAvailableProps) {
      super(props);
      this.state = { type: 'initial' };
      this.handleSubmit = this.handleSubmit.bind(this);
    }

    public static async getInitialProps(ctx: NextPageContext & QueryProps) {
      if (isNext(WrappedComponent)) {
        const pageProps = WrappedComponent.getInitialProps
          ? await WrappedComponent.getInitialProps(ctx)
          : {};

        return { ...pageProps, query: ctx.query };
      }

      return { query: ctx.query };
    }

    public async componentDidMount() {
      const {
        query: { publicId, resource_id },
        session,
      } = this.props;
      try {
        const bookingData = await implUCMerchantRepository(
          session,
        ).BookingService(publicId, parseInt(resource_id, 10));

        this.setState({
          type: 'available',
          success: !bookingData.isPasswordProtected,
          accepting: bookingData.isBookable,
        });
      } catch (e) {
        this.setState({ type: 'error', statusCode: e.response.status });
      }
    }

    public handleSubmit(result: boolean) {
      this.setState({ type: 'available', success: result });
    }

    public render() {
      const {
        query: { resource_id },
        session,
      } = this.props;
      if (this.state.type === 'initial') {
        return null;
      }
      if (this.state.type === 'error') {
        return <Error statusCode={this.state.statusCode} />;
      }
      if (!this.state.success) {
        return (
          <MerchantCourseDetailsPageProtected
            session={session}
            servicePublicId={parseInt(resource_id, 10)}
            onSubmit={this.handleSubmit}
          />
        );
      }

      return (
        <WrappedComponent {...this.props} isAccepting={this.state.accepting} />
      );
    }
  };
}
